import { useQueryClient } from "@tanstack/react-query";

import {
  useGetDealTeaserEmailTemplateV2,
  useUpdateDealTeaserEmailTemplateV2,
} from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { type UpdateDealTeaserEmailTemplateRequest } from "@vega-api/generated/types/updateDealTeaserEmailTemplateRequest";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

export const useDealEmailTemplate = (dealId: string) => {
  const queryClient = useQueryClient();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const { data, isPending, isError, refetch, queryKey } =
    useGetDealTeaserEmailTemplateV2(dealId, {
      query: { enabled: Boolean(dealId) },
    });

  const updateMutation = useUpdateDealTeaserEmailTemplateV2({
    mutation: {
      onSuccess: (response) => {
        queryClient.setQueryData(queryKey, response);
        showToast({
          variant: "success",
          title: m.coInvestments_emailTemplate_saveSuccess(),
        });
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultTitle: m.coInvestments_emailTemplate_saveError(),
        });
      },
    },
  });

  const save = async (data: UpdateDealTeaserEmailTemplateRequest) => {
    try {
      await updateMutation.mutateAsync({ dealId, data });
    } catch {
      // onError shows the toast; swallow so Formik settles isSubmitting.
    }
  };

  return {
    template: data?.data,
    isLoading: isPending,
    isError,
    refetch,
    save,
  };
};
